import React from "react";
import { useEffect, useState } from "react";
import { AuthContinue } from "../misc/auth-continue.component";
import { AuthPaymentContainer } from "../misc/auth-payment-container.component";
import { AuthRestartButton } from "../misc/restart-button.component";
import { ReviewDisplayField } from "../misc/review-display-field.component";
import { StandardSkipDisclosure } from "../misc/disclosures";
import { generateLoanDescription } from "../misc/messaging-utils";
import { constructDebitEpxRequest, constructDebitRequest } from "../../../utils/utils";
import moment from "moment";
import { usd } from "../../../utils/utils";
import Show from "../../../utils/show.component";

export const AuthSkipReview = (props) => {
  const skipDebitResponse = props.authPayment.skipDebitResponse;
  const formValues = props.thisForm && props.thisForm.values;

  const isSkippingTwoMonths = formValues && formValues.skipTwoMonths === "true";

  const feeAmount = isSkippingTwoMonths ? 70 : 35;

  const skipInfo = props.authPayment.selectedLoanSkipInfo;
  const additionalAmount =
    skipInfo.AmountToBringCurrentInfo.Balance < 0
      ? Math.abs(skipInfo.AmountToBringCurrentInfo.Balance)
      : 0;

  const skipPaymentMethod = formValues && formValues.skipPaymentMethod;

  const totalFeeAmount =
    skipPaymentMethod === "Debit Card" ? feeAmount + additionalAmount : feeAmount;

  // If the skip debit response state has been set, the user has already successfully submitted
  // a debit payment and we don't need to submit another one
  const hasSubmittedDebit = skipDebitResponse.hasOwnProperty("PaymentAmount");

  const allSkipDates = props.authPayment.selectedLoanSkipInfo.SkippableDueDates;

  const selectedSkipDates = isSkippingTwoMonths
    ? [allSkipDates[0], allSkipDates[1]]
    : [allSkipDates[0]];

  const nextDueDate = isSkippingTwoMonths
    ? allSkipDates[1].ResumeDueDate
    : allSkipDates[0].ResumeDueDate;

  const useSmallerText = skipPaymentMethod === "Lake Trust Account";
  const accountTextModifier = useSmallerText ? "f14" : "";

  const getPaymentAccountNumber = () => {
    let paymentAccount = null;

    if (skipPaymentMethod === "Debit Card") {
      const cardNumber = formValues && formValues.account_nbr;
      const lastFour = cardNumber.substring(cardNumber.length - 4);

      formValues.AccountHash = null; // Clear AccountHash if using debit card
      paymentAccount = lastFour;
    } else {
      const accountHash = formValues && formValues.AccountHash;
      const internalAccounts = props.authPayment.internalAccounts;
      const selectedAccount = internalAccounts.find((account) => {
        return account.AccountHash === accountHash;
      });

      paymentAccount = selectedAccount.AccountLastFour;
    }

    return paymentAccount;
  };

  const getPaymentAccount = () => {
    let paymentAccount = null;

    const cardTypeFromEndpoint = props.authPayment.skipCardType;

    const cardTypeToDisplay =
      cardTypeFromEndpoint && cardTypeFromEndpoint !== "INVALID"
        ? cardTypeFromEndpoint
        : "Card ending in ";

    if (skipPaymentMethod === "Debit Card") {
      const formattedCardNumber = `${cardTypeToDisplay} *${getPaymentAccountNumber()}`;

      paymentAccount = formattedCardNumber;
    } else {
      const formattedAccountNumber = `Ending in ${getPaymentAccountNumber()}`;

      paymentAccount = formattedAccountNumber;
    }

    return paymentAccount;
  };

  const constructPostSkipRequest = () => {
    const request = {
      SkipTwoMonths: isSkippingTwoMonths,
      FeeAmount: feeAmount,
      AdditionalAmount: skipPaymentMethod === "Debit Card" ? additionalAmount : 0,
      EmailOverride: null,
      TransferFromAccountNumber: formValues.AccountHash,
      WaiveFee: false,
      HasLoanServicingPermissions: false,
      DeclinedEmailReceipt: false,
      // Only for determining method in the saga
      skipPaymentMethod: skipPaymentMethod,
    };

    return request;
  };

  const advance = () => {
    window.scrollTo(0, 0);

    props.callAuthPostSkip(
      constructDebitRequest(formValues, totalFeeAmount, skipPaymentMethod),
      constructPostSkipRequest(),
      hasSubmittedDebit
    );
  };

  useEffect(() => {
    if (props.authPayment.skipPostResponse.hasOwnProperty("PaymentAccountInfo")) {
      props.setProcessStep(3);
    }
  }, [props.authPayment.skipPostResponse]);

  const formatSkipDates = (selectedDatesArray) => {
    const formatted = selectedDatesArray.map((dateObject) => {
      const date = moment(dateObject.SkippableDueDate).format("MM/DD/YYYY");
      return date;
    });

    return formatted;
  };

  const formattedSkipDates = formatSkipDates(selectedSkipDates);
  const showAdditionalAmountFields = skipPaymentMethod === "Debit Card" && additionalAmount;

  return (
    <AuthPaymentContainer title="Review & Submit">
      <div className="text-centered bold f20">
        {`${generateLoanDescription(props.selectedLoan, true)}`}
      </div>
      <div className="my-4 divider"></div>
      <div>
        <ReviewDisplayField title={"Payment(s) To Skip"} value={formattedSkipDates[0]} />
        <Show condition={formattedSkipDates.length > 1}>
          <ReviewDisplayField title={""} value={formattedSkipDates[1]} />
        </Show>
        <ReviewDisplayField
          title={"Payment Account"}
          value={getPaymentAccount()}
          valueClassNames={`minwidth-fit ${accountTextModifier}`}
          titleColumnSize={"col-md-6"}
          valueColumnSize={"col-md-6"}
        />
        <Show condition={showAdditionalAmountFields}>
          <ReviewDisplayField
            title={"Fee Amount"}
            value={usd.format(feeAmount)}
            valueClassNames={"minwidth-fit"}
          />
          <ReviewDisplayField
            title={"Negative Balance Due"}
            value={usd.format(additionalAmount)}
            valueClassNames={"minwidth-fit"}
          />
        </Show>
        <ReviewDisplayField title={"Total Fee Amount"} value={usd.format(totalFeeAmount)} />
        <ReviewDisplayField
          title={"Next Due Date"}
          value={moment(nextDueDate).format("MM/DD/YYYY")}
        />
      </div>
      {StandardSkipDisclosure(
        formattedSkipDates,
        moment(nextDueDate).format("MM/DD/YYYY"),
        getPaymentAccountNumber(),
        totalFeeAmount,
        skipPaymentMethod,
        additionalAmount > 0
      )}
      <AuthContinue type="button" title={"Submit"} onClickFunction={() => advance()} />
      <AuthRestartButton backToLoans={props.backToLoans} />
    </AuthPaymentContainer>
  );
};



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/review/auth-skip-review-fields.component.js